import React, { useEffect, useState } from "react";
import axiosInstance from "../../utils/axiosInstance";
import FormModal from "./FormModal";
import Button from "../ui/Button";

export default function MoveMemberModal({
  isOpen,
  onClose,
  onMoved,
  organisationId,
  customerId,
  member,
}) {
  const [departments, setDepartments] = useState([]);
  const [loadingDepts, setLoadingDepts] = useState(false);
  const [targetId, setTargetId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const user = member?.userId;
  const userName = user ? `${user.firstname || ""} ${user.lastname || ""}`.trim() : "";

  useEffect(() => {
    if (!isOpen) {
      setTargetId("");
      setError("");
      return;
    }

    const fetchDepartments = async () => {
      setLoadingDepts(true);
      setError("");
      try {
        const res = await axiosInstance.get("/admin/organisations", {
          params: { limit: 200 },
        });
        const items = res.data?.items || res.data || [];
        const sameCustomer = items.filter(
          (org) =>
            String(org.customerId?._id || org.customerId) === String(customerId) &&
            String(org._id) !== String(organisationId) &&
            org.status !== "archived"
        );
        setDepartments(sameCustomer);
      } catch (err) {
        setError(err.response?.data?.message || "Failed to load departments");
      } finally {
        setLoadingDepts(false);
      }
    };

    fetchDepartments();
  }, [isOpen, customerId, organisationId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!targetId || !member?._id) return;

    setSubmitting(true);
    setError("");
    try {
      await axiosInstance.post(
        `/admin/organisations/${organisationId}/members/${member._id}/transfer`,
        { targetOrganisationId: targetId }
      );
      onMoved?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to move member");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormModal isOpen={isOpen} onClose={onClose} title="Move to Department">
      <p className="text-sm text-gray-600 mb-4">
        Move <strong>{userName || user?.email}</strong> from this department to another
        department within the same company. Their role and hourly rates will carry over.
        Historical time reports remain in this department.
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="targetDepartment" className="block text-sm font-medium text-gray-700">
            Target department
          </label>
          {loadingDepts ? (
            <p className="text-sm text-gray-500 py-2">Loading departments…</p>
          ) : departments.length === 0 ? (
            <p className="text-sm text-gray-500 py-2">
              No other departments available. You may need customer admin access to move
              members to departments you don&apos;t manage.
            </p>
          ) : (
            <select
              id="targetDepartment"
              value={targetId}
              onChange={(e) => setTargetId(e.target.value)}
              className="w-full border rounded px-3 py-2"
              required
            >
              <option value="">Select department…</option>
              {departments.map((dept) => (
                <option key={dept._id} value={dept._id}>
                  {dept.name}
                </option>
              ))}
            </select>
          )}
        </div>

        {error && <div className="text-red-500 text-sm">{error}</div>}

        <div className="flex justify-end space-x-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-300 rounded"
            disabled={submitting}
          >
            Cancel
          </button>
          <Button
            type="submit"
            disabled={submitting || !targetId || departments.length === 0}
          >
            {submitting ? "Moving…" : "Move Member"}
          </Button>
        </div>
      </form>
    </FormModal>
  );
}
